/**
 * Organizations Management Screen
 *
 * Allows superadmins to view all organizations in the system.
 * Features include organization listing, sorting, details viewing,
 * and create/delete operations.
 */

import { useState, useEffect } from 'react';
import { useSessionState } from '@/hooks/useSessionState';
import { useLocalFilter } from '@/hooks/useLocalFilter';
import { SessionKey } from '@/types/storage';
import { SearchInput } from '@/components/core';
import { useScreenState } from '@/hooks/useScreenState';
import { useAlert } from '@/components/core/Alert';
import { ScreenType } from '@/types/screen.types';
import { Organization } from '@/types/orgs-management.types';
import { OrgsTable } from './OrgsTable';
import { OrgDetailsDialog } from './OrgDetailsDialog';
import { Alert } from '@/components/core/Alert';
import { logger } from '@/utils/logger';
import { useAllOrgsQuery } from '@/hooks/queries';
import { CreateOrgDialog } from './CreateOrgDialog';
import { AsyncButton } from '@/components/ui';
import { IconName } from '@/icons';
import { Action } from '@/business/types/action.types';
import { useOrchestrator } from '@/hooks/useOrchestrator';
import type {
  DeleteOrganizationInput,
  DeleteOrganizationOutput,
  RestoreOrganizationInput,
  RestoreOrganizationOutput,
} from '@/types/generated';

const ORG_SEARCH_KEYS: Array<keyof Organization> = ['title', 'code'];

/**
 * Organizations Management Screen Component
 */
export default function OrgsManagement() {
  const alert = useAlert();

  // State
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization | null>(null);
  const [nameSearch, setNameSearch] = useState('');
  const [isOrgDialogOpen, setIsOrgDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [showDeleted, setShowDeleted] = useSessionState<boolean>(
    SessionKey.LB_ORGS_SHOW_DELETED,
    false
  );

  const { data: orgsData, isLoading: isLoadingOrgs } = useAllOrgsQuery(
    showDeleted || undefined
  );
  const { execute: executeDelete } = useOrchestrator<
    DeleteOrganizationInput,
    DeleteOrganizationOutput
  >(Action.DELETE_ORGANIZATION);
  const { execute: executeRestore } = useOrchestrator<
    RestoreOrganizationInput,
    RestoreOrganizationOutput
  >(Action.RESTORE_ORGANIZATION);
  const organizations: Organization[] = (orgsData?.organizations ??
    []) as Organization[];
  const filteredOrganizations = [
    ...useLocalFilter(organizations, nameSearch, ORG_SEARCH_KEYS),
  ].sort((a, b) => a.title.localeCompare(b.title));

  // Screen state management — TQ handles data loading
  useScreenState({
    metadata: {
      screenType: ScreenType.ORGS_MANAGEMENT,
      requiresAuth: true,
      showHeader: true,
      showSidebar: true,
    },
    onLoad: async (_strategy, state) => {
      logger.info('Organizations Management: Loading organizations');
      state.setSuccess();
    },
    lifecycle: {
      onUnmount: () => {
        logger.info('Organizations Management unmounted');
      },
    },
  });

  // Log when organizations load
  useEffect(() => {
    if (orgsData?.organizations) {
      logger.info('Organizations Management: Organizations loaded', {
        count: orgsData.organizations.length,
      });
    }
  }, [orgsData]);

  // Handle row click to open organization details dialog
  const handleRowClick = (org: Organization) => {
    setSelectedOrganization(org);
    setIsOrgDialogOpen(true);
  };

  // Handle restore organization
  const handleRestoreOrg = (org: Organization) => {
    alert.show({
      type: 'info',
      title: `Restore "${org.title}"?`,
      message: `This will restore the organization "${org.code}".`,
      buttons: [
        { label: 'Cancel', variant: 'outline', onClick: () => {} },
        {
          label: 'Restore',
          variant: 'default',
          onClick: async () => {
            try {
              await executeRestore({
                organization_id: org.id,
              });
            } catch {
              // handled by orchestrator
            }
          },
        },
      ],
    });
  };

  // Handle delete organization
  const handleDeleteOrg = (org: Organization) => {
    alert.show({
      type: 'warning',
      title: `Delete "${org.title}"?`,
      message: `This will soft-delete the organization "${org.code}". It can be restored later.`,
      buttons: [
        { label: 'Cancel', variant: 'outline', onClick: () => {} },
        {
          label: 'Delete',
          variant: 'destructive',
          onClick: async () => {
            try {
              await executeDelete({
                organization_id: org.id,
              });
            } catch {
              // handled by orchestrator
            }
          },
        },
      ],
    });
  };

  return (
    <div className="space-y-4 p-6">
      {/* Info section */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Click column headers to sort. Click any row to view details.
        </div>

        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={e => setShowDeleted(e.target.checked)}
              className="rounded border-muted-foreground/30"
            />
            Show deleted
          </label>
          <span className="text-sm text-muted-foreground">
            Showing {filteredOrganizations.length} of {organizations.length}{' '}
            {organizations.length === 1 ? 'organization' : 'organizations'}
          </span>
          <SearchInput
            value={nameSearch}
            onChange={setNameSearch}
            placeholder="Search by name or code..."
            className="w-56"
          />
          <AsyncButton
            onClick={async () => setIsCreateDialogOpen(true)}
            icon={IconName.PLUS}
          >
            Create Organization
          </AsyncButton>
        </div>
      </div>

      {/* Organizations Table */}
      <OrgsTable
        organizations={filteredOrganizations}
        isLoading={isLoadingOrgs}
        onRowClick={handleRowClick}
        onDelete={handleDeleteOrg}
        onRestore={handleRestoreOrg}
      />

      {/* Organization Details Dialog */}
      <OrgDetailsDialog
        organization={selectedOrganization}
        isOpen={isOrgDialogOpen}
        onClose={() => setIsOrgDialogOpen(false)}
      />

      {/* Create Organization Dialog */}
      <CreateOrgDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
      />

      {/* Alert Component */}
      <Alert {...alert.props} />
    </div>
  );
}
